import { useCallback, useEffect, useRef, useState } from 'react'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { BarcodeFormat, DecodeHintType } from '@zxing/library'
import { mercadolibreApi } from '../../api/mercadolibre'
import { colors, font, text } from '../../design/tokens'
import { fmtFecha } from '../../utils/fecha'
import { BotonPrimario, Card, Cargando, ErrorBox, Seccion, Titulo, fmtMoney } from './comunes'

// Precios de MercadoLibre por EAN desde el celular (Martin, 29 sep 2026:
// iPhone, solo escaneo, solo él, contra LISTA1). La cámara lee el código de
// barras (Safari no tiene BarcodeDetector: lector zxing) y LGR trae nuestro
// artículo y las ofertas del catálogo de ML.
const HINTS = new Map([[DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E]]])

export default function MovilPreciosMl() {
  const videoRef = useRef(null)
  const controlesRef = useRef(null)
  const [escaneando, setEscaneando] = useState(false)
  const [ean, setEan] = useState('')
  const [cargando, setCargando] = useState(false)
  const [r, setR] = useState(null)
  const [error, setError] = useState('')

  const parar = useCallback(() => {
    controlesRef.current?.stop()
    controlesRef.current = null
    setEscaneando(false)
  }, [])
  useEffect(() => parar, [parar])

  const consultar = useCallback(async (codigo) => {
    setEan(codigo); setCargando(true); setError(''); setR(null)
    try {
      const { data } = await mercadolibreApi.precios(codigo)
      setR(data)
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo consultar MercadoLibre.')
    } finally { setCargando(false) }
  }, [])

  const escanear = async () => {
    setError(''); setR(null); setEscaneando(true)
    window.scrollTo(0, 0)
    try {
      const lector = new BrowserMultiFormatReader(HINTS)
      controlesRef.current = await lector.decodeFromConstraints(
        { video: { facingMode: { ideal: 'environment' } } },
        videoRef.current,
        (resultado, _err, controles) => {
          if (!resultado) return
          controles.stop()
          controlesRef.current = null
          setEscaneando(false)
          if (navigator.vibrate) navigator.vibrate(60)
          consultar(resultado.getText())
        },
      )
    } catch (e) {
      setEscaneando(false)
      setError(e?.name === 'NotAllowedError'
        ? 'El navegador no dio permiso para usar la cámara. Habilitala en Ajustes → Safari → Cámara, o tipeá el código.'
        : 'No se pudo abrir la cámara. Tipeá el código.')
    }
  }

  const manual = (e) => {
    e.preventDefault()
    const codigo = ean.replace(/\D+/g, '')
    if (codigo.length >= 8) consultar(codigo)
  }

  return (
    <div>
      <Titulo>Precios ML</Titulo>

      <div style={{ ...st.visor, display: escaneando ? 'block' : 'none' }}>
        <video ref={videoRef} style={st.video} muted playsInline />
        <div style={st.guia} />
      </div>
      {escaneando
        ? <button onClick={parar} style={st.btnSec}>Cancelar</button>
        : <BotonPrimario onClick={escanear}>📷 Escanear código</BotonPrimario>}

      <form onSubmit={manual} style={st.manual}>
        <input value={ean} onChange={e => setEan(e.target.value)} placeholder="o tipeá el EAN" inputMode="numeric" style={st.input} />
        <button type="submit" style={{ ...st.btnSec, width: 'auto', flexShrink: 0 }}>Buscar</button>
      </form>

      <ErrorBox>{error}</ErrorBox>
      {cargando && <Cargando />}
      {r && <Resultado r={r} onOtro={escanear} />}
    </div>
  )
}

function Resultado({ r, onOtro }) {
  const lista1 = r.nuestros.find(n => n.lista1 != null)?.lista1 ?? null
  const minimos = r.productos_ml.map(p => p.mas_barata?.precio).filter(v => v != null)
  const minMl = minimos.length ? Math.min(...minimos) : null
  const todos = r.productos_ml.flatMap(p => p.precios || p.ofertas.map(o => o.precio))
  const totalOfertas = todos.length
  const debajo = lista1 != null ? todos.filter(v => v < lista1).length : 0

  return (
    <>
      <Card style={{ marginTop: 12 }}>
        <div style={st.ean}>EAN {r.ean}</div>
        <div style={st.comparacion}>
          <div>
            <div style={st.etiqueta}>Nosotros (LISTA1)</div>
            <div style={st.monto}>{lista1 != null ? fmtMoney(lista1) : '—'}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={st.etiqueta}>Más barato en ML</div>
            <div style={st.monto}>{minMl != null ? fmtMoney(minMl) : '—'}</div>
          </div>
        </div>
        {lista1 != null && minMl != null && <Diferencia lista1={lista1} ml={minMl} />}
        {lista1 != null && totalOfertas > 0 && (
          <div style={st.debajo}>{debajo} de {totalOfertas} {totalOfertas === 1 ? 'oferta está' : 'ofertas están'} por debajo de nuestro precio</div>
        )}
      </Card>

      <Seccion titulo="Nosotros">
        {r.nuestros.length === 0 && <p style={st.nota}>No hay artículos con este EAN en LGR.</p>}
        {r.nuestros.map(n => (
          <div key={n.sku} style={st.renglon}>
            <div style={{ minWidth: 0 }}>
              <div style={st.nombre}>{n.detalle}{n.anio ? ` · ${n.anio}` : ''}</div>
              <div style={st.sub}><span style={st.mono}>{n.sku}</span>{n.sin_stock ? ' · sin stock' : ''}</div>
            </div>
            <div style={st.precio}>{n.lista1 != null ? fmtMoney(n.lista1) : '—'}</div>
          </div>
        ))}
      </Seccion>

      {r.productos_ml.length === 0 && <Seccion titulo="MercadoLibre"><p style={st.nota}>Este EAN no está en el catálogo de MercadoLibre.</p></Seccion>}
      {r.productos_ml.map(p => (
        <Seccion key={p.id} titulo={`MercadoLibre · ${p.cantidad_ofertas} ${p.cantidad_ofertas === 1 ? 'oferta' : 'ofertas'}`}>
          <a href={p.link} target="_blank" rel="noreferrer" style={st.link}>{p.nombre || p.id}</a>
          {p.sin_publicaciones && <p style={st.nota}>Está en el catálogo pero sin publicaciones activas.</p>}
          {p.error_items && <p style={st.nota}>No se pudieron leer las ofertas ({p.error_items}).</p>}
          {p.ofertas.map(o => (
            <a key={o.item_id} href={o.link} target="_blank" rel="noreferrer" style={st.oferta}>
              <div style={{ minWidth: 0 }}>
                <div style={st.precio}>{fmtMoney(o.precio)}</div>
                <div style={st.sub}>{[o.envio_gratis === true ? 'Envío gratis' : o.envio_gratis === false ? 'Envío con costo' : null, o.tienda_oficial ? 'Tienda oficial' : null].filter(Boolean).join(' · ') || '—'}</div>
              </div>
              <span style={st.ver}>Ver en ML ›</span>
            </a>
          ))}
        </Seccion>
      ))}

      {r.consultas_previas?.length > 0 && (
        <Seccion titulo="Consultas anteriores">
          {r.consultas_previas.map((c, i) => (
            <div key={i} style={st.renglon}>
              <span style={{ ...st.sub, ...st.mono }}>{fmtFecha(c.fecha)}</span>
              <span style={st.sub}>LGR <span style={st.mono}>{c.lista1 != null ? fmtMoney(c.lista1) : '—'}</span> · ML <span style={st.mono}>{c.ml_min != null ? fmtMoney(c.ml_min) : '—'}</span></span>
            </div>
          ))}
        </Seccion>
      )}
      <BotonPrimario onClick={onOtro} style={{ marginTop: 14 }}>📷 Escanear otro</BotonPrimario>
    </>
  )
}

// + = estamos más caros que la oferta más barata de ML.
function Diferencia({ lista1, ml }) {
  const d = (lista1 / ml - 1) * 100
  const caros = d > 0.05
  return (
    <div style={{ ...st.dif, background: caros ? colors.warnBg : colors.okBg, color: caros ? colors.warn : colors.ok }}>
      {caros ? `Estamos ${pct(d)} % más caros` : d < -0.05 ? `Estamos ${pct(Math.abs(d))} % más baratos` : 'Mismo precio'}
    </div>
  )
}

const pct = (n) => n.toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

const mono = { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums' }
const st = {
  mono,
  visor: { position: 'relative', borderRadius: 14, overflow: 'hidden', background: colors.codeBg, marginBottom: 10 },
  video: { width: '100%', display: 'block', aspectRatio: '4 / 3', objectFit: 'cover' },
  guia: { position: 'absolute', left: '12%', right: '12%', top: '45%', height: 2, background: colors.errStrong, opacity: 0.8 },
  btnSec: { padding: '13px 16px', borderRadius: 12, border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, fontSize: 15, fontWeight: 600, fontFamily: font.sans, cursor: 'pointer', width: '100%', boxSizing: 'border-box', minHeight: 44 },
  manual: { display: 'flex', gap: 8, marginTop: 10 },
  input: { flex: 1, minWidth: 0, padding: '12px 12px', borderRadius: 12, border: `1px solid ${colors.textPlh}`, fontSize: 16, color: colors.text, background: colors.bg, outline: 'none', boxSizing: 'border-box', ...mono },
  ean: { fontSize: text.sec, color: colors.textMeta, ...mono },
  comparacion: { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '8px 12px', marginTop: 8 },
  etiqueta: { fontSize: text.sec, color: colors.textSec },
  monto: { fontSize: 20, fontWeight: 700, color: colors.text, ...mono },
  dif: { marginTop: 10, padding: '8px 10px', borderRadius: 10, fontSize: 14, fontWeight: 600, textAlign: 'center', fontFamily: font.sans },
  renglon: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${colors.borderSoft}` },
  oferta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '12px 0', minHeight: 44, borderBottom: `1px solid ${colors.borderSoft}`, textDecoration: 'none', color: colors.text },
  nombre: { fontSize: text.body, color: colors.text, fontFamily: font.sans },
  sub: { fontSize: text.sec, color: colors.textSec, fontFamily: font.sans },
  precio: { fontSize: 15, fontWeight: 600, color: colors.text, flexShrink: 0, ...mono },
  debajo: { marginTop: 8, fontSize: text.sec, color: colors.textSec, textAlign: 'center', fontFamily: font.sans },
  ver: { fontSize: text.sec, fontWeight: 600, color: colors.link, flexShrink: 0, fontFamily: font.sans },
  nota: { fontSize: text.sec, color: colors.textSec, margin: '6px 0 0' },
  link: { display: 'block', fontSize: text.body, fontWeight: 600, color: colors.link, textDecoration: 'none', marginBottom: 4 },
}
